import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@cbrebuild/blocks';
import { Link, useHistory } from 'react-router-dom';
import VOUCHER_ROUTES from '../../../page-templates/voucher/voucher-subroutes';
import LabeledItem from '../../../nucleus/labeled-item/labeled-item';
import { fetchFilesDownloadLink, fetchVoucherFiles } from '../../../redux/actions/deal-files-actions';
import { fetchOfficeTypes } from '../../../redux/actions/voucher/voucher-actions';
import VoucherFileListItem from '../files/voucher-file-list-item';
import LoadMore from '../../../nucleus/load-more/load-more';
import RequiredLabeledItem from './RequiredLabeledItem';
import ConnectorIdDisplay from '../../connector/connector-id-display';
import { isBrokerVerificationActive } from '../../../utils/broker-verification-utils';
import { getCommissionAttachmentFiles } from '../../../utils/commission-agreement-utils';
import { getAttachmentName } from '../../../page-templates/voucher/voucher-verify-utils';

const GettingStartedReview = () => {
  const {
    deal, voucher, voucherFiles, voucherFilesCount, voucherFilesNext, isLoadingVoucherFiles, offices, dealFiles, brokerVerificationFlagOn,
  } = useSelector(state => ({
    deal: state.deal.deal,
    voucher: state.voucher.voucher,
    voucherFiles: state.dealFiles?.voucherFiles?.files || [],
    voucherFilesCount: state.dealFiles?.voucherFiles?.count || 0,
    voucherFilesNext: state.dealFiles?.voucherFiles?.next,
    isLoadingVoucherFiles: state.dealFiles?.isLoadingVoucherFiles,
    // Every deal file ReviewTab has loaded (voucher + deal-only), so a commission agreement
    // tagged on a deal-only file still counts.
    dealFiles: state.dealFiles?.files,
    offices: (state.voucher.officeTypes || []).map(o => ({
      display_name: o.managing_office_name,
      value: o.managing_office_name,
    })),
    brokerVerificationFlagOn: isBrokerVerificationActive(
      state.userOptions?.userOptions?.feature_flags,
      state.voucher.voucher?.is_early_invoice,
    ),
  }));
  const dispatch = useDispatch();
  const history = useHistory();
  const url = (VOUCHER_ROUTES[0].path).replace(':id', voucher.deal);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (voucher.deal) {
      dispatch(fetchVoucherFiles({
        deal: voucher.deal,
        ordering: '-modified',
        page: 1,
        page_size: 25,
      }));
      setPage(1);
    }
  }, [dispatch, voucher.deal]);

  // Powers the Lead Office dropdown's options in the "Before you submit" panel — same
  // OPTIONS-backed office list the live GettingStartedTab's own Lead Office dropdown uses.
  useEffect(() => {
    dispatch(fetchOfficeTypes());
  }, [dispatch]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    dispatch(fetchVoucherFiles({
      deal: voucher.deal,
      ordering: '-modified',
      page: nextPage,
      page_size: 25,
    }));
  };

  const remainingCount = voucherFilesCount - voucherFiles.length;

  // Format Account Client display value
  let accountClientItem = null;
  if (deal.is_account_management_client != null) {
    accountClientItem = deal.is_account_management_client ? 'Yes' : 'No';
  }
  const accountClientCodeMissing = deal.is_account_management_client === true
    && !(deal.account_code && deal.account_description);

  // Both answers live on the deal (Transaction), the same place Getting Started saves them.
  let fullyExecutedLeaseItem = null;
  if (deal.is_fully_executed_lease != null) {
    fullyExecutedLeaseItem = deal.is_fully_executed_lease ? 'Yes' : 'No';
  }
  let hasCommissionAgreementItem = null;
  if (deal.has_commission_agreement != null) {
    hasCommissionAgreementItem = deal.has_commission_agreement ? 'Yes' : 'No';
  }
  // Files tagged for the current answer: the commission agreement (Yes) or supporting documents (No).
  const commissionAttachmentFiles = getCommissionAttachmentFiles(dealFiles, deal.has_commission_agreement);
  const commissionAgreementAttachments = commissionAttachmentFiles.map(file => file.id);
  const commissionAttachmentDocuments = commissionAttachmentFiles.map(file => ({ id: file.id, name: getAttachmentName(file) }));
  const commissionAttachmentMissing = deal.has_commission_agreement === true && commissionAgreementAttachments.length === 0;

  // Snapshots, once per voucher visit, whether the commission agreement was already answered
  // Yes WITH an attachment already uploaded when this section first renders -- if so, nothing
  // needs attention and "Upload Commission Agreement" should stay hidden like any other
  // already-complete field, even though it unmounts/remounts every time the Yes/No answer is
  // toggled. If it *wasn't* already Yes+attached at that first moment, reaching Yes+attached
  // afterwards happened during this visit (e.g. uploading a file, or toggling No -> Yes after
  // already having uploaded one) and must keep showing.
  const attachmentAlreadyCompleteOnLoadRef = useRef(null);
  // "No" needs no attachment, so the supporting-documents upload is only offered when No is
  // picked during this visit, not when it was already saved as No before Review opened.
  const answeredNoOnLoadRef = useRef(null);
  useEffect(() => {
    attachmentAlreadyCompleteOnLoadRef.current = null;
    answeredNoOnLoadRef.current = null;
  }, [voucher.id]);
  useEffect(() => {
    if (answeredNoOnLoadRef.current === null) answeredNoOnLoadRef.current = deal.has_commission_agreement === false;
    if (attachmentAlreadyCompleteOnLoadRef.current !== null) return;
    attachmentAlreadyCompleteOnLoadRef.current = deal.has_commission_agreement === true
      && commissionAgreementAttachments.length > 0;
  }, [deal.has_commission_agreement, commissionAgreementAttachments]);
  const commissionAttachmentForceRegister = commissionAttachmentMissing
    || attachmentAlreadyCompleteOnLoadRef.current === false;
  const supportingDocumentsForceRegister = answeredNoOnLoadRef.current === false;

  /*
    #1 Is this deal Confidential? - Optional
    #2 This is a Referral Deal - Optional
    #3 Did CBRE represent both sides of this deal? - Optional
    #4 Lead Office - Required
    #5 Sourced By - Required (has default value)
    #6 Office Name - Conditionally Required
    #7 Account Client - Required
    #8 Client Code - Description - Required when Account Client = Yes
   */

  return (
    <div className="voucher-review-group getting-started-review">
      <div className="navigation">
        <Link className="h2 link" to={url}>Getting Started</Link>
        <IconButton iconName="edit" onClick={() => { history.push(url); }} />
      </div>
      <div className="content">
        {voucher.is_early_invoice &&
          <RequiredLabeledItem
            label="Reason for Early Invoice"
            item={voucher.reason_for_early_invoice}
            isReview
            fieldId="getting-started-reason-for-early-invoice"
            section="Getting Started"
            controlType="textarea"
            editTarget={{ reduxAction: 'updateVoucher', fieldPath: 'reason_for_early_invoice' }}
          />}
        <LabeledItem label="Deal Type" item={deal.deal_type} />
        <LabeledItem label="Rep Role" item={deal.rep_role} />
        <ConnectorIdDisplay deal={deal} />
        <LabeledItem label="Confidential" item={deal.is_confidential ? 'Yes' : 'No'} />
        <LabeledItem label="Referral" item={deal.is_referral_deal ? 'Yes' : 'No'} />
        <LabeledItem label="Dual Repped" item={deal.is_dual_rep_deal ? 'Yes' : 'No'} />
        {(deal.deal_type === 'lease' || deal.deal_type === 'sale') &&
          <RequiredLabeledItem
            label="Lead Office"
            item={deal.voucher_lead_office}
            isReview
            fieldId="getting-started-lead-office"
            section="Getting Started"
            controlType="select"
            options={offices}
            editTarget={{ reduxAction: 'updateDeal', fieldPath: 'voucher_lead_office' }}
          />}
        <LabeledItem label="Sourced By" item={deal.sourced_by} />
        {(deal.deal_type === 'lease' || deal.deal_type === 'sale') &&
          <RequiredLabeledItem
            label="Office Name"
            item={deal.voucher_office_name}
            notRequired={deal.sourced_by !== 'Office' && deal.sourced_by !== 'Affiliate'}
            isReview
            fieldId="getting-started-office-name"
            section="Getting Started"
            editTarget={{ reduxAction: 'updateDeal', fieldPath: 'voucher_office_name' }}
          />}
        <RequiredLabeledItem
          label="Account Client"
          item={accountClientItem}
          isReview
          fieldId="getting-started-account-client"
          section="Getting Started"
          controlType="boolean"
          // "Account Client" and "Client Code - Description" below are one logical field, not
          // two -- force this into the panel even though "Yes" itself has no error, so it
          // doesn't stay hidden while its own Client Code - Description still shows as required.
          forceRegister={accountClientCodeMissing}
          editTarget={{ reduxAction: 'updateDeal', fieldPath: 'is_account_management_client' }}
        />
        {deal.is_account_management_client === true && (
          <RequiredLabeledItem
            label="Client Code - Description"
            item={deal.account_code && deal.account_description
              ? `${deal.account_code} - ${deal.account_description}`
              : null}
            notRequired={false}
            isReview
            fieldId="getting-started-client-code-description"
            section="Getting Started"
            controlType="clientCode"
            editTarget={{ reduxAction: 'updateDeal', fieldPath: 'account_code_description' }}
          />
        )}
        {/* Always mounted (like every other field in this panel, e.g. Lead Office above) --
            `RequiredLabeledItem`'s own `isReview`-gated `everNeededAttentionRef` latch decides
            whether/when it actually shows up, and keeps it visible with a checkmark once it
            does, same as any other field, instead of this component unmounting/remounting it
            (which previously wiped out that latch and made an already-shown field vanish the
            moment its answer changed, e.g. switching "Is there a commission agreement?" from
            Yes to No). */}
        {brokerVerificationFlagOn && (
          <RequiredLabeledItem
            label="Is this a fully executed lease?"
            item={fullyExecutedLeaseItem}
            isReview
            fieldId="getting-started-fully-executed-lease"
            section="Getting Started"
            controlType="boolean"
            editTarget={{ reduxAction: 'updateDeal', fieldPath: 'is_fully_executed_lease' }}
          />
        )}
        {brokerVerificationFlagOn && (
          <RequiredLabeledItem
            label="Is there a commission agreement?"
            item={hasCommissionAgreementItem}
            isReview
            fieldId="getting-started-commission-agreement"
            section="Getting Started"
            controlType="boolean"
            // The Yes/No question and its attachment upload below are one logical field, not
            // two -- force this into the panel even though "Yes" itself has no error, so it
            // doesn't stay hidden while its own attachment upload still shows as required.
            forceRegister={commissionAttachmentMissing}
            editTarget={{ reduxAction: 'updateDeal', fieldPath: 'has_commission_agreement' }}
          />
        )}
        {brokerVerificationFlagOn && deal.has_commission_agreement === true && (
          <RequiredLabeledItem
            label="Upload Commission Agreement"
            item={commissionAgreementAttachments}
            documents={commissionAttachmentDocuments}
            missingMessage="Document needed"
            format="attachmentList"
            isReview
            fieldId="getting-started-commission-agreement-attachment"
            section="Getting Started"
            controlType="commissionAttachment"
            // Only force this into the panel while it's actually missing, or while it became
            // Yes+attached during this visit -- an attachment that was already saved before this
            // visit started must stay hidden on remount (e.g. after a No -> Yes toggle), not
            // reappear just because the field happens to remount.
            forceRegister={commissionAttachmentForceRegister}
            editTarget={{
              reduxAction: 'updateCommissionAttachments',
              fieldPath: 'commission_agreement_attachments',
              targetId: deal.id,
            }}
          />
        )}
        {brokerVerificationFlagOn && deal.has_commission_agreement === false && (
          <RequiredLabeledItem
            label="Upload Supporting Documentation"
            item={commissionAgreementAttachments}
            format="attachmentList"
            notRequired
            hideDisplay
            isReview
            fieldId="getting-started-supporting-documentation"
            section="Getting Started"
            controlType="commissionAttachment"
            forceRegister={supportingDocumentsForceRegister}
            editTarget={{
              reduxAction: 'updateCommissionAttachments',
              fieldPath: 'supporting_documentation_attachments',
              targetId: deal.id,
            }}
          />
        )}
        <ul className="download-list">
          {voucherFiles.map(file => (
            <VoucherFileListItem
              fetchFilesDownloadLink={(id, forceDownload) => dispatch(fetchFilesDownloadLink(id, forceDownload))}
              file={file}
              key={file.id}
              voucher={voucher}
              dealType={deal.deal_type}
              showSparkleIcon
              isAddButton={false}
              isDropDown={false}
            />
          ))}
        </ul>
        {remainingCount > 0 && (
          <LoadMore
            isLoading={isLoadingVoucherFiles}
            handleLoadMore={handleLoadMore}
            hasNextPage={voucherFilesNext || remainingCount > 0}
            label={`Load More (${remainingCount})`}
          />
        )}
      </div>
    </div>
  );
};

export default GettingStartedReview;
